import { useMemo, useState, useEffect, useRef } from "react";
import { Search, Scale, BookOpen, FlaskConical, ChevronDown } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { PENAL_CODE, IMAGES } from "@/data/content";
import {
  DEFINED_TERMS, PROCEDURE_SECTIONS, AGGRAVATING, BUSINESS_SECTIONS,
  CONTROLLED_ITEMS, ILLEGAL_ITEMS, ILLEGAL_EXCEPTION, TITLE_II_CHAPTERS,
} from "@/data/penalLegal";
import { useSiteConfig } from "@/context/SiteConfig";
import { api } from "@/lib/api";

const CLS_COLOR = {
  Infraction: "text-teal border-teal/30 bg-teal/5",
  Misdemeanor: "text-[#E0B056] border-[#E0B056]/30 bg-[#E0B056]/5",
  Felony: "text-ember border-ember/30 bg-ember/5",
  Capital: "text-destructive border-destructive/40 bg-destructive/10",
};

// ---- small building blocks -------------------------------------------------
const SectionHeading = ({ icon: Icon, overline, title, id, refs }) => (
  <div ref={(el) => (refs.current[id] = el)} className="scroll-mt-28 mb-4">
    {overline && <p className="font-feature text-[10px] uppercase tracking-[0.3em] text-[#8F877C] mb-2">{overline}</p>}
    <div className="flex items-center gap-3 border-b border-teal/30 pb-3">
      {Icon && <Icon className="h-5 w-5 text-teal shrink-0" />}
      <h2 className="font-display text-2xl sm:text-3xl font-light text-bone">{title}</h2>
    </div>
  </div>
);

const Prose = ({ items }) => (
  <div className="space-y-4">
    {items.map((t, i) => (
      <p key={i} className="font-body text-[15px] leading-relaxed text-[#c8c0b4]">{t}</p>
    ))}
  </div>
);

const IndexLink = ({ target, jump, children }) => (
  <button onClick={() => jump(target)} data-testid={`penal-index-${target}`}
    className="block w-full text-left px-3 py-1.5 text-sm border-l-2 border-transparent text-[#b8b0a4] hover:text-teal hover:border-teal/60 transition-colors">
    {children}
  </button>
);

// ---- page ------------------------------------------------------------------
export default function PenalCode() {
  useTitle("Penal Code | Aurora Springs RP | RedM Roleplay", "The Aurora Springs RP penal code — defined terms, charges, procedures and sentencing guidelines for in-character law on our 1895 RedM roleplay server.");
  const { img, penal_text } = useSiteConfig();
  const [q, setQ] = useState("");
  const [codes, setCodes] = useState(PENAL_CODE);
  const [terms, setTerms] = useState(DEFINED_TERMS.map((t) => ({ ...t })));
  const [procedures, setProcedures] = useState(PROCEDURE_SECTIONS);
  const [business, setBusiness] = useState(BUSINESS_SECTIONS);
  const [aggr, setAggr] = useState(AGGRAVATING);
  const [controlled, setControlled] = useState(CONTROLLED_ITEMS);
  const [illegal, setIllegal] = useState(ILLEGAL_ITEMS);
  const [open, setOpen] = useState(null);
  const refs = useRef({});

  useEffect(() => {
    api.get("/penal").then(({ data }) => { if (data?.length) setCodes(data); }).catch(() => {});
    api.get("/penal-terms").then(({ data }) => { if (data?.length) setTerms(data.map((t) => ({ term: t.term, def: t.definition }))); }).catch(() => {});
    api.get("/penal-aggravating").then(({ data }) => { if (data?.length) setAggr(data.map((a) => ({ code: a.code, circ: a.circ, desc: a.description, fine: a.fine, jail: a.jail }))); }).catch(() => {});
    api.get("/penal-controlled").then(({ data }) => { if (data?.length) setControlled(data); }).catch(() => {});
    api.get("/penal-illegal").then(({ data }) => {
      if (!data?.length) return;
      const map = new Map();
      data.forEach((r) => { if (!map.has(r.category)) map.set(r.category, []); map.get(r.category).push(r.name); });
      setIllegal([...map.entries()].map(([name, items], i) => ({ id: `t3-${i}`, name, items })));
    }).catch(() => {});
    api.get("/penal-provisions").then(({ data }) => {
      if (!data?.length) return;
      const toSecs = (rows) => rows.map((r) => ({ heading: r.heading, body: (r.body || "").split("\n").filter(Boolean) }));
      setProcedures(toSecs(data.filter((r) => r.section === "procedures")));
      setBusiness(toSecs(data.filter((r) => r.section === "business")));
    }).catch(() => {});
  }, []);

  const exceptionText = penal_text?.illegal_exception || ILLEGAL_EXCEPTION;
  const aggravatingNote = penal_text?.aggravating_note || "Aggravating Circumstances may be added on top of individual charges.";
  const pt = penal_text || {};

  const s = q.trim().toLowerCase();
  const searching = s.length > 0;

  const rows = useMemo(() => codes.filter((r) =>
    r.charge.toLowerCase().includes(s) || r.code.toLowerCase().includes(s) ||
    r.cls.toLowerCase().includes(s) || (r.definition || "").toLowerCase().includes(s)), [s, codes]);

  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => { const k = (r.code[0] in TITLE_II_CHAPTERS) ? r.code[0] : "1"; (g[k] ||= []).push(r); });
    return Object.keys(g).sort().map((k) => ({ key: k, label: TITLE_II_CHAPTERS[k], rows: g[k] }));
  }, [rows]);

  const jump = (k) => refs.current[k]?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <PageHeader overline="Law of the Land" title="Penal Code" image={img("penal", IMAGES.townCattle)}
        sub="The definitions, procedures, offenses and controlled goods enforced by the Sheriff's Department. Use the Legal Index to navigate, or search the offense charts below." />

      <section className="mx-auto max-w-7xl px-6 sm:px-10 py-14 grid gap-10 lg:grid-cols-[250px_1fr] items-start">
        {/* Legal Index */}
        <aside className="lg:sticky lg:top-24 hidden lg:block" data-testid="penal-index">
          <p className="flex items-center gap-2 font-feature text-sm uppercase tracking-[0.2em] text-teal mb-5"><Scale className="h-4 w-4" /> Legal Index</p>

          <p className="font-feature text-[10px] uppercase tracking-[0.25em] text-[#8F877C] mb-2">Title I — General Provisions</p>
          <ul className="mb-5 space-y-0.5">
            <li><IndexLink jump={jump} target="t1-terms">Defined Terms</IndexLink></li>
            <li><IndexLink jump={jump} target="t1-procedures">Procedures, Limitations & Reliefs</IndexLink></li>
            <li><IndexLink jump={jump} target="t1-aggravating">Aggravating Circumstances</IndexLink></li>
            <li><IndexLink jump={jump} target="t1-business">Employment & Business Licensure</IndexLink></li>
          </ul>

          <p className="font-feature text-[10px] uppercase tracking-[0.25em] text-[#8F877C] mb-2">Title II — Offenses</p>
          <ul className="mb-5 space-y-0.5">
            {Object.entries(TITLE_II_CHAPTERS).map(([k, label]) => (
              <li key={k}><IndexLink jump={jump} target={k}>{label.replace(/^Ch\.\s*\d+\s*—\s*/, "")}</IndexLink></li>
            ))}
          </ul>

          <p className="font-feature text-[10px] uppercase tracking-[0.25em] text-[#8F877C] mb-2">Title III — Controlled & Illegal Items</p>
          <ul className="space-y-0.5">
            <li><IndexLink jump={jump} target="t3-controlled">Controlled Items</IndexLink></li>
            {illegal.map((c) => (
              <li key={c.id}><IndexLink jump={jump} target={c.id}>{c.name}</IndexLink></li>
            ))}
          </ul>
        </aside>

        {/* Content */}
        <div>
          <div className="relative mb-10 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F877C]" />
            <input data-testid="penal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search charges or codes..."
              className="w-full bg-surface/60 border border-[#2A201A] pl-11 pr-4 py-3 font-mono text-sm text-bone placeholder:text-[#5c554c] focus:outline-none focus:border-teal transition-colors" />
          </div>

          {/* Title I — hidden while searching offenses */}
          {!searching && (
            <div className="mb-16" data-testid="penal-title-1">
              {/* Defined Terms */}
              <SectionHeading icon={BookOpen} overline={pt.terms_overline || "Title I · Chapter One"} title={pt.terms_title || "Defined Terms"} id="t1-terms" refs={refs} />
              <dl className="grid gap-px bg-[#2A201A] border border-[#2A201A] mb-14 sm:grid-cols-[200px_1fr]">
                {terms.map((t) => (
                  <div key={t.term} className="contents" data-testid={`penal-term-${t.term.split(" ")[0].toLowerCase()}`}>
                    <dt className="bg-obsidian/80 px-5 py-4 font-feature text-sm uppercase tracking-wider text-teal">{t.term}</dt>
                    <dd className="bg-surface/30 px-5 py-4 font-body text-[15px] leading-relaxed text-[#c8c0b4]">{t.def}</dd>
                  </div>
                ))}
              </dl>

              {/* Procedures & Limitations */}
              <SectionHeading icon={Scale} overline={pt.proc_overline || "Title I · Chapter Two"} title={pt.proc_title || "Procedures, Limitations & Reliefs"} id="t1-procedures" refs={refs} />
              <div className="space-y-8 mb-12">
                {procedures.map((sec) => (
                  <div key={sec.heading}>
                    <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-[#E0B056] mb-3">{sec.heading}</h3>
                    <Prose items={sec.body} />
                  </div>
                ))}
              </div>

              {/* Aggravating Circumstances */}
              <div ref={(el) => (refs.current["t1-aggravating"] = el)} className="scroll-mt-28 mb-14">
                <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-[#E0B056] mb-2">{pt.aggr_heading || "D) Aggravating Circumstances"}</h3>
                <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4] mb-4">
                  {(pt.aggr_badge ?? "Under Review") && <span className="font-mono text-xs uppercase tracking-wider text-ember border border-ember/40 bg-ember/10 px-2 py-0.5 mr-2">{pt.aggr_badge ?? "Under Review"}</span>}
                  {aggravatingNote}
                </p>
                <div className="border border-[#2A201A] overflow-x-auto">
                  <div className="hidden sm:grid grid-cols-[70px_200px_1fr_130px_130px] gap-4 px-6 py-3 bg-obsidian/80 border-b border-[#2A201A] font-feature text-xs uppercase tracking-widest text-[#8F877C]">
                    <span>Code</span><span>Circumstance</span><span>Description</span><span>Fine Added</span><span>Jail Added</span>
                  </div>
                  {aggr.map((a, i) => (
                    <div key={a.code} data-testid={`penal-ac-${a.code}`}
                      className={`grid grid-cols-2 sm:grid-cols-[70px_200px_1fr_130px_130px] gap-2 sm:gap-4 px-6 py-4 items-start border-b border-[#2A201A] last:border-0 ${i % 2 ? "bg-surface/20" : ""}`}>
                      <span className="font-mono text-sm text-ember">{a.code}</span>
                      <span className="font-body text-bone">{a.circ}</span>
                      <span className="font-body text-sm text-[#c8c0b4]">{a.desc}</span>
                      <span className="font-mono text-sm text-[#E0B056]">{a.fine}</span>
                      <span className="font-mono text-sm text-[#8F877C]">{a.jail}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Licensure */}
              <SectionHeading icon={Scale} overline={pt.biz_overline || "Title I · Chapter Three"} title={pt.biz_title || "Employment & Business Licensure"} id="t1-business" refs={refs} />
              <div className="space-y-8">
                {business.map((sec) => (
                  <div key={sec.heading}>
                    <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-[#E0B056] mb-3">{sec.heading}</h3>
                    <Prose items={sec.body} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Title II — Offenses */}
          <div data-testid="penal-title-2">
            {!searching && <SectionHeading icon={Scale} overline="Title II" title="Offenses" id="t2" refs={refs} />}
            {groups.length === 0 && <div className="py-16 text-center font-mono text-sm text-[#8F877C]">No charges match &ldquo;{q}&rdquo;.</div>}
            {groups.map((g) => (
              <div key={g.key} ref={(el) => (refs.current[g.key] = el)} className="mb-12 scroll-mt-28">
                <div className="border border-teal/30 bg-teal/5 px-5 py-3 mb-3">
                  <h3 className="font-display text-xl sm:text-2xl font-light text-teal">{g.label}</h3>
                </div>
                <div className="border border-[#2A201A]">
                  <div className="hidden sm:grid grid-cols-[110px_1fr_150px_100px_130px] gap-4 px-6 py-3 bg-obsidian/80 border-b border-[#2A201A] font-feature text-xs uppercase tracking-widest text-[#8F877C]">
                    <span>Ref #</span><span>Charge</span><span>Class</span><span>Fine</span><span>Sentence</span>
                  </div>
                  {g.rows.map((r, i) => {
                    const isOpen = open === r.code;
                    return (
                      <div key={r.id || r.code} className={`border-b border-[#2A201A] last:border-0 ${i % 2 ? "bg-surface/20" : ""}`}>
                        <button type="button" onClick={() => setOpen(isOpen ? null : r.code)}
                          data-testid={`penal-row-${r.code}`} aria-expanded={isOpen}
                          className="w-full text-left grid grid-cols-2 sm:grid-cols-[110px_1fr_150px_100px_130px] gap-2 sm:gap-4 px-6 py-4 items-center hover:bg-surface/60 transition-colors">
                          <span className="font-mono text-sm text-teal">{r.code}</span>
                          <span className="font-body text-bone flex items-center gap-2">
                            {r.charge}
                            {(r.definition || r.penalty) && <ChevronDown className={`h-3.5 w-3.5 text-[#8F877C] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                          </span>
                          <span><span className={`inline-block font-mono text-[10px] uppercase tracking-wider border px-2 py-1 ${CLS_COLOR[r.cls]}`}>{r.cls}</span></span>
                          <span className="font-mono text-sm text-bone">{r.fine}</span>
                          <span className="font-mono text-sm text-[#8F877C]">{r.time}</span>
                        </button>
                        {isOpen && (r.definition || r.penalty) && (
                          <div data-testid={`penal-def-${r.code}`} className="px-6 pb-5 pt-1 sm:pl-[134px] bg-obsidian/40 space-y-3">
                            {r.definition && <p className="font-body text-sm leading-relaxed text-[#c8c0b4]">{r.definition}</p>}
                            {r.penalty && (
                              <div className="border-l-2 border-ember/50 bg-ember/5 px-4 py-3" data-testid={`penal-penalty-${r.code}`}>
                                <p className="font-feature text-[10px] uppercase tracking-[0.2em] text-ember mb-1">Penalty</p>
                                <p className="font-body text-sm leading-relaxed text-[#c8c0b4]">{r.penalty}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Title III — Controlled & Illegal Items */}
          {!searching && (
            <div className="mt-16" data-testid="penal-title-3">
              <SectionHeading icon={FlaskConical} overline="Title III" title="Controlled & Illegal Items" id="t3" refs={refs} />

              {/* Chapter One — Controlled Items */}
              <div ref={(el) => (refs.current["t3-controlled"] = el)} className="scroll-mt-28 mb-14" data-testid="penal-controlled">
                <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-[#E0B056] mb-2">Chapter One · Controlled Items</h3>
                <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4] mb-5 max-w-3xl">
                  Distribution of these items is controlled by the Government of Aurora Springs County. Possession by anyone beyond a listed &ldquo;Permitted Person&rdquo; subjects the Citizen to Trafficking charges (Title II &sect;1.3 or &sect;1.4). Producers of controlled substances must label their product (see Title I, Chapter 3).
                </p>
                <div className="border border-[#2A201A]">
                  <div className="hidden sm:grid grid-cols-[1fr_1.2fr] gap-4 px-6 py-3 bg-obsidian/80 border-b border-[#2A201A] font-feature text-xs uppercase tracking-widest text-[#8F877C]">
                    <span>Item</span><span>Permitted Person</span>
                  </div>
                  {controlled.map((c, i) => (
                    <div key={c.item} data-testid={`penal-controlled-${i}`}
                      className={`grid grid-cols-1 sm:grid-cols-[1fr_1.2fr] gap-1 sm:gap-4 px-6 py-4 items-start border-b border-[#2A201A] last:border-0 ${i % 2 ? "bg-surface/20" : ""}`}>
                      <span className="font-body text-bone">{c.item}</span>
                      <span className="font-body text-sm text-[#c8c0b4]">{c.permitted}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chapter Two — Illegal Items */}
              <h3 className="font-feature text-sm uppercase tracking-[0.2em] text-ember mb-2">Chapter Two · Illegal Items</h3>
              <p className="font-body text-[15px] leading-relaxed text-[#c8c0b4] mb-6 max-w-3xl">
                Illegal to possess in any quantity or circumstance. Possession subjects the Citizen to Trafficking charges (Title II &sect;1.3 or &sect;1.4).
              </p>
              <div className="grid gap-6 sm:grid-cols-3">
                {illegal.map((c) => (
                  <div key={c.id} ref={(el) => (refs.current[c.id] = el)} data-testid={`penal-cat-${c.id}`}
                    className="scroll-mt-28 border border-ember/20 bg-ember/5 p-6">
                    <h4 className="font-display text-lg font-light text-bone mb-4">{c.name}</h4>
                    <ul className="space-y-2">
                      {c.items.map((it) => (
                        <li key={it} className="font-mono text-xs text-[#c8c0b4] flex items-start gap-2">
                          <span className="text-ember mt-0.5">&bull;</span>{it}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-body text-sm italic text-[#8F877C] border-l-2 border-teal/40 pl-4">
                <span className="not-italic font-feature text-xs uppercase tracking-wider text-teal mr-2">Exception</span>{exceptionText}
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
